import { useRef, useState } from 'react';
import { toast } from '@wit-support/ui';
import { useApplySettings, usePreviewSettings } from '../queries.js';
import { classifyLiveError } from './live-error.js';
/** Ключ идемпотентности: 32 символа `[0-9a-f]` — подходит под правило сервера (16–64, без дефисов). */
export const newIdempotencyKey = () => crypto.randomUUID().replace(/-/g, '');
/**
 * «Проверить → подтвердить → применить» (спека S2 §5.2): предпросмотр ничего не пишет; запись шлёт
 * `expected` ИЗ ЭТОГО предпросмотра и тот же ключ идемпотентности (повторный клик после сетевого
 * сбоя — тот же ключ). Новый предпросмотр — новый `previewId` и новый ключ.
 */
export function useLiveEditFlow(args) {
    const previewM = usePreviewSettings(args.accountId, args.level, args.entityId);
    const applyM = useApplySettings(args.accountId, args.level, args.entityId);
    const [confirm, setConfirm] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [applyError, setApplyError] = useState(null);
    /** Запись прошла, но Meta уточнила настройки или сверка не подтверждена: человек должен это прочитать. */
    const [resultNote, setResultNote] = useState(null);
    const inFlight = useRef(false);
    /** «Проверить изменения». */
    const submit = (change, reason) => {
        if (inFlight.current)
            return;
        setPreviewError(null);
        setApplyError(null);
        previewM.mutate({ change, ...(reason ? { reason } : {}) }, {
            onSuccess: (preview) => {
                if (preview.noop || preview.changes.length === 0) {
                    toast.info('Ничего не изменилось — значения те же, что в Meta.');
                    args.onDone();
                    return;
                }
                setConfirm({ change, reason, preview, idempotencyKey: newIdempotencyKey() });
            },
            onError: (e) => setPreviewError(classifyLiveError(e)),
        });
    };
    /** «Применить N изменений». */
    const apply = (acknowledge) => {
        if (!confirm || inFlight.current)
            return;
        inFlight.current = true;
        setApplyError(null);
        applyM.mutate({
            change: confirm.change,
            ...(confirm.reason ? { reason: confirm.reason } : {}),
            idempotencyKey: confirm.idempotencyKey,
            expected: {
                baselineHash: confirm.preview.baselineHash,
                diffHash: confirm.preview.diffHash,
                previewId: confirm.preview.previewId,
            },
            ...(acknowledge.length > 0 ? { acknowledge } : {}),
        }, {
            onSuccess: (r) => {
                inFlight.current = false;
                setConfirm(null);
                if (!r.readBack.ok || !r.readBack.verified) {
                    // не «успех»: показываем, что именно Meta сохранила (или что результат не подтверждён)
                    setResultNote({ result: r, summary: args.successText(r) });
                    return;
                }
                toast.success(args.successText(r));
                args.onDone();
            },
            onError: (e) => {
                inFlight.current = false;
                setApplyError(classifyLiveError(e));
            },
        });
    };
    /** «Назад» из подтверждения — форма остаётся с введёнными значениями. */
    const back = () => {
        if (applyM.isPending)
            return;
        setConfirm(null);
        setApplyError(null);
    };
    /** «Обновить и проверить заново» / «Перечитать из Meta»: ошибка и подтверждение сбрасываются, настройки читаются заново. */
    const refresh = async () => {
        setConfirm(null);
        setApplyError(null);
        setPreviewError(null);
        await args.onRefresh();
    };
    /** «Понятно» в окне результата. */
    const dismissResult = () => {
        setResultNote(null);
        args.onDone();
    };
    return {
        resultNote,
        dismissResult,
        confirm,
        previewing: previewM.isPending,
        applying: applyM.isPending,
        previewError,
        applyError,
        submit,
        apply,
        back,
        refresh,
    };
}
//# sourceMappingURL=use-live-edit-flow.js.map